import { Component, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FIKO_KONTROL_STIL } from './fiko-kontrol-stil';

/**
 * Fiko3DHizliMenu içinde gösterilen gelişmiş/futuristik eskiz paneli.
 * hizliMenu3DEk component-outlet üzerinden render edilir.
 * Tüm boyutlandırma + constraint'leri inline düzenlenebilir gösterir.
 */
@Component({
  selector: 'fikoyt-sketch-hizli-ek',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="fk fk-panel" *ngIf="element" style="width:188px;">
    <div class="fk-baslik">
      <span class="fk-bi">{{ tipIkon() }}</span>
      <span class="fk-ad">{{ tipAd() }}</span>
      <span class="fk-rozet" *ngIf="element.yapi">YAPI</span>
    </div>
    <div class="fk-govde">
      <div class="fk-satir" *ngFor="let a of boyutlar(); trackBy: trkBy">
        <label>{{ a.etiket }}</label>
        <div class="fk-inp">
          <input class="fk-in" type="number" [ngModel]="a.deger" (ngModelChange)="boyutla(a.tip, $event)" [step]="a.step || 1" [min]="a.min ?? 0" />
          <span class="fk-birim">{{ a.tip === 'kenar' ? '×' : 'mm' }}</span>
        </div>
      </div>
      <div class="fk-deger" *ngIf="element.tip === 'cizgi'"><b>{{ aci() }}°</b><span>eğim</span></div>
    </div>

    <div *ngIf="element.tip === 'cizgi'">
      <div class="fk-bolum-bas">Kısıtlar</div>
      <div class="fk-sira">
        <button class="fk-btn fk-btn-genis" (click)="yatay()">⊢ Yatay</button>
        <button class="fk-btn fk-btn-genis" (click)="dikey()">⊥ Dikey</button>
      </div>
    </div>

    <div class="fk-sira" *ngIf="patlatilabilir()">
      <button class="fk-btn fk-btn-genis" (click)="patlat()" title="Parametrik bağı kopar → ayrı çizgiler"><span class="fk-bi">⛓</span> Patlat (bağı kopar)</button>
    </div>
    <div class="fk-altbar">
      <button class="fk-btn teal fk-btn-genis fk-btn-dikey" (click)="olcule()"><span class="fk-bi">⟺</span>{{ olculu() ? 'Ölçü var' : 'Ölçülendir' }}</button>
      <button class="fk-btn grey fk-btn-genis fk-btn-dikey" (click)="yapi()"><span class="fk-bi">{{ element.yapi ? '─' : '⋯' }}</span>{{ element.yapi ? 'Normal' : 'Yapı' }}</button>
      <button class="fk-btn red fk-btn-genis fk-btn-dikey" (click)="sil()"><span class="fk-bi">🗑</span>Sil</button>
    </div>
  </div>
  `,
  styles: [FIKO_KONTROL_STIL, `.fk-bi{font-size:13px;line-height:1;}`]
})
export class FikoSketchHizliEk {
  @Input() element: any;
  @Input() yonetici: any;

  constructor(public CD: ChangeDetectorRef) {}

  trkBy = (_: number, a: any) => a.tip;

  tipAd(): string {
    return ({ cizgi: 'Çizgi', dikdortgen: 'Dikdörtgen', cember: 'Çember', yay: 'Yay', nokta: 'Nokta', cokgen: 'Çokgen', slot: 'Slot' } as any)[this.element?.tip] || 'Eleman';
  }
  tipIkon(): string {
    return ({ cizgi: '╱', dikdortgen: '▭', cember: '◯', yay: '⌒', nokta: '•', cokgen: '⬡', slot: '▱' } as any)[this.element?.tip] || '◆';
  }

  boyutlar(): { etiket: string; tip: string; deger: number; step?: number; min?: number }[] {
    const el = this.element; if (!el) return [];
    if (el.tip === 'cizgi') { const [a, b] = el.noktalar; return [{ etiket: 'Uzunluk', tip: 'uzunluk', deger: +Math.hypot(b.x - a.x, b.y - a.y).toFixed(2) }]; }
    if (el.tip === 'cember') return [{ etiket: 'Yarıçap', tip: 'yaricap', deger: +(el.r || 0).toFixed(2) }, { etiket: 'Çap', tip: 'cap', deger: +((el.r || 0) * 2).toFixed(2) }];
    if (el.tip === 'yay') return [{ etiket: 'Yarıçap', tip: 'yaricap', deger: +(el.r || 0).toFixed(2) }];
    if (el.tip === 'dikdortgen') { const [a, b] = el.noktalar; return [{ etiket: 'Genişlik', tip: 'genislik', deger: +Math.abs(b.x - a.x).toFixed(2) }, { etiket: 'Yükseklik', tip: 'yukseklik', deger: +Math.abs(b.y - a.y).toFixed(2) }]; }
    if (el.tip === 'cokgen') return [{ etiket: 'Kenar Sayısı', tip: 'kenar', deger: el.kenarSayisi || 6, step: 1, min: 3 }, { etiket: 'Yarıçap', tip: 'yaricap', deger: +(el.r || 0).toFixed(2) }];
    if (el.tip === 'slot') { const [c1, c2] = el.noktalar; return [{ etiket: 'Uzunluk', tip: 'slotuzun', deger: +Math.hypot(c2.x - c1.x, c2.y - c1.y).toFixed(2) }, { etiket: 'Genişlik', tip: 'slotgen', deger: +((el.r || 0) * 2).toFixed(2) }]; }
    return [];
  }

  aci(): string {
    const el = this.element; if (el?.tip !== 'cizgi') return '0';
    const [a, b] = el.noktalar;
    return ((Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI + 360) % 180).toFixed(1);
  }

  olculu(): boolean {
    return !!this.yonetici?.sketch?.olculer?.some((o: any) => o.elemanId === this.element?.id);
  }

  boyutla(tip: string, deger: number): void { this.yonetici?.panelBoyut(this.element, tip, +deger); this.CD.markForCheck(); }
  yatay(): void { this.yonetici?.panelYatay(this.element); this.CD.markForCheck(); }
  dikey(): void { this.yonetici?.panelDikey(this.element); this.CD.markForCheck(); }
  olcule(): void { this.yonetici?.panelOlcule(this.element); this.CD.markForCheck(); }
  yapi(): void { this.yonetici?.panelYapi(this.element); this.CD.markForCheck(); }
  sil(): void { this.yonetici?.panelSil(this.element); }
  patlatilabilir(): boolean { return !!this.yonetici?.patlatilabilir?.(this.element); }
  patlat(): void { this.yonetici?.panelPatlat(this.element); }
}
